import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#332E5C', // DAC Group's deep blue-purple
      light: '#4A4570',
      dark: '#221B3F',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#00CED1', // DAC Group's bright teal/cyan
      light: '#33D6D9',
      dark: '#009092',
      contrastText: '#ffffff'
    },
    success: {
      main: '#10b981', // Modern green
      light: '#34d399',
      dark: '#059669'
    },
    warning: {
      main: '#f59e0b', // Modern amber
      light: '#fbbf24',
      dark: '#d97706'
    },
    error: {
      main: '#ef4444', // Modern red
      light: '#f87171',
      dark: '#dc2626'
    },
    info: {
      main: '#332E5C', // Use primary for info
      light: '#4A4570',
      dark: '#221B3F'
    },
    background: {
      default: '#fafafa', // Slightly warmer white
      paper: '#ffffff'
    },
    text: {
      primary: '#1f2937', // Rich dark gray
      secondary: '#6b7280', // Medium gray
      disabled: '#9ca3af' // Light gray
    },
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    }
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 8px 30px rgba(0, 0, 0, 0.12)',
    '0 12px 40px rgba(0, 0, 0, 0.15)',
    '0 16px 50px rgba(0, 0, 0, 0.2)',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none'
  ] as any,
  typography: {
    fontFamily: [
      '"Montserrat"',
      '"Inter"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
      letterSpacing: '-0.025em'
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: 1.3,
      letterSpacing: '-0.025em'
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '-0.025em'
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.025em'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: '-0.025em'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
      letterSpacing: '-0.025em'
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#374151'
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#6b7280'
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#374151'
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#6b7280'
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: '#9ca3af'
    }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: '1px solid rgba(226, 232, 240, 0.6)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #332E5C 0%, #00CED1 100%)',
            opacity: 0,
            transition: 'opacity 0.3s ease'
          },
          '&:hover': {
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-4px)',
            borderColor: 'rgba(51, 46, 92, 0.2)',
            '&::before': {
              opacity: 1
            }
          }
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: 24
          }
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          backgroundColor: 'rgba(248, 250, 252, 0.5)',
          borderTop: '1px solid rgba(226, 232, 240, 0.6)'
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 12,
          border: '1px solid rgba(226, 232, 240, 0.8)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        },
        elevation2: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        },
        elevation3: {
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '12px 24px',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          letterSpacing: '0.02em',
          minHeight: '44px',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)'
          },
          '&:active': {
            transform: 'translateY(0px)'
          }
        },
        contained: {
          background: 'linear-gradient(135deg, #332E5C 0%, #221B3F 100%)',
          color: '#ffffff',
          border: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #4A4570 0%, #332E5C 100%)',
            boxShadow: '0 6px 20px rgba(51, 46, 92, 0.4)'
          },
          '&:disabled': {
            background: '#e5e7eb',
            color: '#9ca3af'
          }
        },
        outlined: {
          borderColor: '#332E5C',
          borderWidth: '2px',
          color: '#332E5C',
          backgroundColor: 'rgba(51, 46, 92, 0.05)',
          '&:hover': {
            borderColor: '#332E5C',
            borderWidth: '2px',
            backgroundColor: 'rgba(51, 46, 92, 0.1)',
            boxShadow: '0 4px 12px rgba(51, 46, 92, 0.2)'
          }
        },
        text: {
          color: '#332E5C',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: 'rgba(51, 46, 92, 0.08)',
            transform: 'none'
          }
        }
      },
      variants: [
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            background: 'linear-gradient(135deg, #00CED1 0%, #009092 100%)',
            color: '#ffffff',
            '&:hover': {
              background: 'linear-gradient(135deg, #33D6D9 0%, #00CED1 100%)',
              boxShadow: '0 6px 20px rgba(0, 206, 209, 0.4)'
            }
          }
        }
      ]
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          borderBottom: 'none',
          '& .MuiToolbar-root': {
            color: '#ffffff'
          },
          '& .MuiButton-root': {
            color: '#ffffff'
          }
        }
      }
    }
  }
});

export default theme; 